import {
  AppType,
  Provider as GadgetProvider,
  useGadget,
} from "@gadgetinc/react-shopify-app-bridge";
import { NavMenu } from "@shopify/app-bridge-react";
import { useEffect, useState, lazy, Suspense } from "react";
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
// Lazy-loaded route components for better performance
const Index = lazy(() => import("../routes/index"));
// Preload homepage chunk during OAuth so it's ready when auth completes
import("../routes/index");
const Popup = lazy(() => import("../routes/popup"));
// Preload editor chunk so it's ready when user opens campaign modal
import("../routes/popup");
// Hidden for initial launch — uncomment to re-enable experiments
// const ExperimentPage = lazy(() => import("../routes/experiment"));
// const ExperimentsPage = lazy(() => import("../routes/experiments"));
const IntegrationsPage = lazy(() => import("../routes/integrations"));
const SurveyResultsPage = lazy(() => import("../routes/survey-results"));
const BillingPage = lazy(() => import("../routes/billing"));
const DataPrivacyPage = lazy(() => import("../routes/data-privacy"));
const GA4SetupPage = lazy(() => import("../routes/ga4-setup"));
const AnalyticsPage = lazy(() => import("../routes/analytics"));
const CampaignAnalyticsPage = lazy(() => import("../routes/campaign-analytics"));
const TemplatesPage = lazy(() => import("../routes/templates"));
import { api } from "../api";
import { ShopProvider } from "../providers";
import RouteLoader from "./RouteLoader";
import DashboardSkeleton from "./DashboardSkeleton";
import CampaignEditorSkeleton from "./CampaignEditor/CampaignEditorSkeleton";
import ChatWidget from "./ChatWidget";

function Error404() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      process.env.GADGET_PUBLIC_SHOPIFY_APP_URL &&
      location.pathname ===
        new URL(process.env.GADGET_PUBLIC_SHOPIFY_APP_URL).pathname
    )
      return navigate("/", { replace: true });
  }, [location.pathname]);

  return (
    <s-page heading="Page not found">
      <s-section accessibilityLabel="Page not found">
        <s-stack direction="block" gap="base" alignItems="center" padding="large">
          <s-heading>This page doesn't exist</s-heading>
          <s-paragraph>The page you're looking for can't be found. Head back to the dashboard to continue.</s-paragraph>
          <s-button onClick={() => navigate("/")}>Go to Dashboard</s-button>
        </s-stack>
      </s-section>
    </s-page>
  );
}

// Safari-specific router initialization
let routerInstance = null;

function getRouter() {
  if (!routerInstance) {
    routerInstance = createBrowserRouter(
      createRoutesFromElements(
        <Route path="/" element={<Layout />}>
          <Route path="/" index element={
            <Suspense fallback={<DashboardSkeleton />}>
              <Index />
            </Suspense>
          } />
          <Route path="/templates" element={
            <Suspense fallback={<RouteLoader />}>
              <TemplatesPage />
            </Suspense>
          } />
          <Route path="/popup" element={
            <Suspense fallback={<CampaignEditorSkeleton />}>
              <Popup />
            </Suspense>
          } />
          <Route path="/billing" element={
            <Suspense fallback={<RouteLoader />}>
              <BillingPage />
            </Suspense>
          } />
          {/* Hidden for initial launch — uncomment to re-enable experiments
          <Route path="/experiments" element={
            <Suspense fallback={<RouteLoader />}>
              <ExperimentsPage />
            </Suspense>
          } />
          <Route path="/experiments/:experimentId" element={
            <Suspense fallback={<RouteLoader />}>
              <ExperimentPage />
            </Suspense>
          } />
          */}
          <Route path="/integrations" element={
            <Suspense fallback={<RouteLoader />}>
              <IntegrationsPage />
            </Suspense>
          } />
          <Route path="/survey-results" element={
            <Suspense fallback={<RouteLoader />}>
              <SurveyResultsPage />
            </Suspense>
          } />
          <Route path="/data-privacy" element={
            <Suspense fallback={<RouteLoader />}>
              <DataPrivacyPage />
            </Suspense>
          } />
          <Route path="/analytics" element={
            <Suspense fallback={<RouteLoader />}>
              <AnalyticsPage />
            </Suspense>
          } />
          <Route path="/analytics/campaigns/:campaignId" element={
            <Suspense fallback={<RouteLoader />}>
              <CampaignAnalyticsPage />
            </Suspense>
          } />
          <Route path="/ga4-setup" element={
            <Suspense fallback={<RouteLoader />}>
              <GA4SetupPage />
            </Suspense>
          } />
          <Route path="*" element={<Error404 />} />
        </Route>
      )
    );
  }
  return routerInstance;
}

function App() {
  const [router, setRouter] = useState(null);
  
  useEffect(() => {
    // Safari requires delayed router initialization
    const timer = setTimeout(() => {
      setRouter(getRouter());
    }, 0);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (!router) {
    return (
      <s-stack
        direction="block"
        alignItems="center"
        justifyContent="center"
        gap="base"
        padding="large"
        style={{ minHeight: "60vh" }}
      >
        <s-spinner accessibilityLabel="Loading application" size="large-100" />
        <s-text color="subdued">Loading…</s-text>
      </s-stack>
    );
  }
  
  return <RouterProvider router={router} />;
}

export default App;

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Try to get Shopify API key from various sources
  let shopifyApiKey = window.gadgetConfig?.apiKeys?.shopify || 
                      window.gadget?.config?.apiKeys?.shopify ||
                      process.env.GADGET_PUBLIC_SHOPIFY_API_KEY;
  
  if (!shopifyApiKey) {
    console.error("Missing Shopify API key configuration. Available sources:", {
      gadgetConfig: !!window.gadgetConfig,
      gadgetConfigApiKeys: !!window.gadgetConfig?.apiKeys,
      gadgetObject: !!window.gadget,
      envVar: !!process.env.GADGET_PUBLIC_SHOPIFY_API_KEY
    });
    
    return (
      <s-page>
        <s-paragraph>
          Loading configuration... If this persists, please refresh the page.
        </s-paragraph>
      </s-page>
    );
  }

  const config = {
    type: AppType.Embedded,
    shopifyApiKey: shopifyApiKey,
    api: api,
  };

  return (
    <GadgetProvider {...config}>
      <AuthenticatedApp />
    </GadgetProvider>
  );
}

function AuthenticatedApp() {
  // we use `isAuthenticated` to render pages once the OAuth flow is complete!
  const { isAuthenticated, loading } = useGadget();
  
  if (loading) {
    return (
      <s-stack
        direction="block"
        alignItems="center"
        justifyContent="center"
        gap="base"
        padding="large"
        style={{ minHeight: "60vh" }}
      >
        <s-spinner accessibilityLabel="Loading application" size="large-100" />
        <s-text color="subdued">Loading…</s-text>
      </s-stack>
    );
  }
  
  return isAuthenticated ? <EmbeddedApp /> : <UnauthenticatedApp />;
}

function EmbeddedApp() {
  const location = useLocation();
  const isPopup = location.pathname === '/popup';

  return (
    <ShopProvider>
      {!isPopup && (
        <NavMenu>
          <Link to="/" rel="home">
            Home
          </Link>
          <Link to="/templates">Template Library</Link>
          <Link to="/billing">Billing</Link>
          <Link to="/integrations">Integrations</Link>
          <Link to="/analytics">Analytics</Link>
          {/* <Link to="/ga4-setup">Google Analytics</Link> */}
          <Link to="/survey-results">Survey Results</Link>
          <Link to="/data-privacy">Data Privacy</Link>
        </NavMenu>
      )}
      <Outlet />
      <ChatWidget />
    </ShopProvider>
  );
}

function UnauthenticatedApp() {
  return (
    <s-page heading="App">
      <s-paragraph>
        App can only be viewed in the Shopify Admin.
      </s-paragraph>
    </s-page>
  );
}
